/**
 * A small, fast, seeded pseudo-random generator (mulberry32).
 *
 * The simulation must be deterministic and replayable, so it never reads
 * `Math.random()`. All randomness in `sim/` flows through a {@link SeededRng}
 * owned by the {@link World}: the same seed always yields the same sequence,
 * and the generator's `state` round-trips through serialization so a restored
 * world resumes the exact stream it was on.
 */
export class SeededRng {
  /** Current 32-bit internal state; advanced by every {@link next} call. */
  private state: number;

  constructor(seed: number) {
    // Coerce to an unsigned 32-bit integer so any number is a valid seed.
    this.state = seed >>> 0;
  }

  /** Restore a generator from a previously captured {@link getState} value. */
  static fromState(state: number): SeededRng {
    const rng = new SeededRng(0);
    rng.state = state >>> 0;
    return rng;
  }

  /** The current internal state, for serialization. */
  getState(): number {
    return this.state;
  }

  /**
   * Restore the internal state in place (deserialization). Unlike
   * {@link fromState} it mutates this generator rather than creating a new one,
   * so a {@link World} can rehydrate its existing {@link SeededRng} reference and
   * resume the exact stream it was on.
   */
  setState(state: number): void {
    this.state = state >>> 0;
  }

  /** Next float in the half-open range [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Next integer in the half-open range [0, max). `max` must be positive. */
  nextInt(max: number): number {
    return Math.floor(this.next() * max);
  }
}
